// src/pages/Productos.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import GlassCard from '../components/UI/GlassCard';
import Table from '../components/UI/Table';
import Modal from '../components/UI/Modal';
import { 
  Package, 
  Plus, 
  Edit2, 
  Trash2, 
  AlertCircle,
  Barcode,
  DollarSign,
  Archive,
  Zap,
  CheckCircle2,
  TrendingUp,
  LineChart,
  FileText,
  PieChart as PieIcon,
  BarChart3,
  ArrowUpRight,
  Target
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer, 
  Cell 
} from 'recharts';
import { toast } from 'react-hot-toast';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import Swal from 'sweetalert2';

const SafeTippy = ({ children, ...props }) => {
    const [ref, setRef] = useState(null);
    return (
        <>
            <Tippy {...props} reference={ref} />
            {React.cloneElement(React.Children.only(children), { ref: setRef })}
        </>
    );
};

const Productos = () => {
    const { can } = useAuth();
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    
    // Form states
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        precio_costo: '',
        precio_venta: '',
        stock: '',
        codigo_barras: '',
        aplica_ivu: 1
    });

    const fetchProductos = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/backend/api/productos/index.php');
            if (response.data.success) {
                setProductos(response.data.data);
            }
        } catch (error) {
            toast.error('Error al cargar inventario');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProductos();
    }, []);

    const handleOpenModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setFormData({
                nombre: product.nombre,
                descripcion: product.descripcion,
                precio_costo: product.precio_costo,
                precio_venta: product.precio_venta,
                stock: product.stock,
                codigo_barras: product.codigo_barras || '',
                aplica_ivu: parseInt(product.aplica_ivu)
            });
        } else {
            setEditingProduct(null);
            setFormData({
                nombre: '',
                descripcion: '',
                precio_costo: '',
                precio_venta: '',
                stock: '0',
                codigo_barras: '',
                aplica_ivu: 1
            });
        }
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!formData.nombre || !formData.precio_venta || !formData.precio_costo) {
            return toast.error('Nombre, Costo y Precio son obligatorios');
        }

        try {
            const method = editingProduct ? 'PUT' : 'POST';
            const data = editingProduct ? { ...formData, id: editingProduct.id } : formData;
            const response = await axios({
                method,
                url: '/backend/api/productos/index.php',
                data
            });

            if (response.data.success) {
                toast.success(editingProduct ? 'Producto actualizado' : 'Producto creado');
                setShowModal(false);
                fetchProductos();
            }
        } catch (error) {
            toast.error('Error al guardar producto');
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Eliminar producto?',
            text: "El producto se desactivará pero permanecerá en los registros históricos.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Sí, eliminar',
            background: 'rgba(15, 23, 42, 0.95)',
            color: '#fff'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`/backend/api/productos/index.php?id=${id}`);
                if (response.data.success) {
                    toast.success('Producto desactivado');
                    fetchProductos();
                }
            } catch (error) {
                toast.error('Error al eliminar');
            }
        }
    };

    const calculateMargin = (cost, sale) => {
        const c = parseFloat(cost) || 0;
        const s = parseFloat(sale) || 0;
        if (s === 0) return 0;
        return ((s - c) / s) * 100;
    };

    const calculateProfit = (cost, sale) => {
        const c = parseFloat(cost) || 0;
        const s = parseFloat(sale) || 0;
        return s - c;
    };

    // 📊 Métricas para Dashboard
    const totalInversion = productos.reduce((acc, p) => acc + (p.stock * p.precio_costo), 0);
    const totalVentaPotencial = productos.reduce((acc, p) => acc + (p.stock * p.precio_venta), 0);
    const totalUtilidad = totalVentaPotencial - totalInversion;
    const stockCritico = productos.filter(p => p.stock <= 5).length;
    
    // Preparar data para la gráfica (Top 5 por valor de inventario)
    const chartData = [...productos]
        .sort((a, b) => (b.stock * b.precio_venta) - (a.stock * a.precio_venta))
        .slice(0, 5)
        .map(p => ({
            name: p.nombre.length > 12 ? p.nombre.substring(0, 12) + '...' : p.nombre,
            valor: parseFloat((p.stock * p.precio_venta).toFixed(2)),
            stock: p.stock
        }));

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen text-white">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                <div>
                    <h1 className="text-4xl lg:text-5xl font-black flex items-center gap-4">
                        <Package className="text-primary-light" size={42} /> 
                        Inventario de <span className="text-primary-light italic font-medium">Productos</span>
                    </h1>
                    <p className="text-white/40 mt-2 text-sm font-medium flex items-center gap-2">
                        <Archive size={14} className="text-emerald-400" />
                        Gestión de stock, costos y márgenes de ganancia
                    </p>
                </div>
                
                <div className="flex w-full md:w-auto gap-4 items-center">
                    <SafeTippy content="Generar listado maestro de inventario con costos y márgenes" theme="glass-premium" animation="scale">
                        <button 
                            onClick={() => {
                                const token = localStorage.getItem('token');
                                window.open(`/backend/api/reportes/productos_pdf.php?token=${token}`, '_blank');
                            }}
                            className="glass px-6 h-[52px] rounded-2xl flex items-center gap-3 text-white hover:bg-white/10 transition-all border border-white/10 active:scale-95 group shadow-xl"
                        >
                            <FileText size={20} className="text-secondary group-hover:scale-110 transition-transform" />
                            <span className="text-[14px] font-bold">REPORTE PDF</span>
                        </button>
                    </SafeTippy>

                    {can('Productos', 'crear') && (
                        <button 
                            onClick={() => handleOpenModal()}
                            className="premium-btn-primary flex items-center gap-3 px-8 h-[52px] shadow-xl shadow-primary/20 shrink-0"
                        >
                            <Plus size={20} />
                            <span>Añadir Producto</span>
                        </button>
                    )}
                </div>
            </header>

            {/* 📊 KPI DASHBOARD SECTION */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <GlassCard className="border-white/5 relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-primary/10 rounded-full blur-2xl group-hover:bg-primary/20 transition-all"></div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-primary/10 text-primary-light">
                            <Archive size={24} />
                        </div>
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Inversión Stock</span>
                            <div className="text-2xl font-black text-white">${totalInversion.toLocaleString()}</div>
                        </div>
                    </div>
                </GlassCard>

                <GlassCard className="border-white/5 relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/20 transition-all"></div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-400">
                            <TrendingUp size={24} />
                        </div>
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Utilidad Proyectada</span>
                            <div className="text-2xl font-black text-emerald-400">+${totalUtilidad.toLocaleString()}</div>
                        </div>
                    </div>
                </GlassCard>

                <GlassCard className="border-white/5 relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-rose-500/10 rounded-full blur-2xl group-hover:bg-rose-500/20 transition-all"></div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-rose-500/10 text-rose-400">
                            <AlertCircle size={24} />
                        </div>
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Stock Crítico</span>
                            <div className="text-2xl font-black text-rose-400">{stockCritico} <span className="text-xs font-medium text-white/20">items</span></div>
                        </div>
                    </div>
                </GlassCard>

                <GlassCard className="border-white/5 relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl group-hover:bg-amber-500/20 transition-all"></div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-amber-500/10 text-amber-400">
                            <Target size={24} />
                        </div>
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Ticket Promedio</span>
                            <div className="text-2xl font-black text-white">${(totalVentaPotencial / (productos.length || 1)).toFixed(2)}</div>
                        </div>
                    </div>
                </GlassCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                {/* 📉 CHART SECTION */}
                <GlassCard className="lg:col-span-1 border-white/5" title="Top 5 Valor de Inventario">
                    <div className="h-[300px] mt-6">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30 }}>
                                <XAxis type="number" hide />
                                <YAxis dataKey="name" type="category" width={80} axisLine={false} tickLine={false} tick={{fill: 'rgba(255,255,255,0.5)', fontSize: 10}} />
                                <RechartsTooltip 
                                    contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                                    itemStyle={{ color: '#fff' }}
                                    cursor={{fill: 'rgba(255,255,255,0.05)'}}
                                />
                                <Bar dataKey="valor" radius={[0, 4, 4, 0]}>
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={index === 0 ? '#8b5cf6' : '#6366f1'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-4 p-4 rounded-2xl bg-white/5 border border-white/5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <PieIcon size={14} className="text-primary-light" />
                            <span className="text-[10px] font-bold text-white/40 uppercase">Distribución de Capital</span>
                        </div>
                        <ArrowUpRight size={14} className="text-white/20" />
                    </div>
                </GlassCard>

                {/* 📋 TABLE CONTAINER (Now 2/3 width) */}
                <div className="lg:col-span-2 space-y-8">
                    <GlassCard padding="0" className="shadow-2xl border-white/5 overflow-hidden h-full">
                        <Table 
                            headers={['Producto', 'Costo', 'Venta', 'Margen', 'Stock', 'Acciones']}
                            data={productos.map(p => {
                            const margin = calculateMargin(p.precio_costo, p.precio_venta);
                            const profit = calculateProfit(p.precio_costo, p.precio_venta);
                            
                            return {
                                _raw: p,
                                producto: (
                                    <div className="flex flex-col">
                                        <span className="font-black text-lg text-white group-hover:text-primary-light transition-colors">{p.nombre}</span>
                                        <div className="flex items-center gap-2 mt-1">
                                            <Barcode size={10} className="text-white/20" />
                                            <span className="text-[10px] font-bold text-white/30 uppercase tracking-widest">{p.codigo_barras || 'SIN SKU'}</span>
                                        </div>
                                    </div>
                                ),
                                costo: (
                                    <span className="text-white/60 font-medium">${parseFloat(p.precio_costo).toFixed(2)}</span>
                                ),
                                precio: (
                                    <div className="bg-emerald-500/10 px-4 py-2 rounded-xl border border-emerald-500/20 w-fit">
                                        <span className="text-emerald-400 font-black text-xl">${parseFloat(p.precio_venta).toFixed(2)}</span>
                                    </div>
                                ),
                                margen: (
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2">
                                            <TrendingUp size={12} className={margin > 30 ? 'text-emerald-400' : 'text-amber-400'} />
                                            <span className={`font-black ${margin > 30 ? 'text-emerald-400' : 'text-amber-400'}`}>
                                                {margin.toFixed(1)}%
                                            </span>
                                        </div>
                                        <span className="text-[10px] text-white/20 font-bold uppercase tracking-widest mt-1">
                                            +${profit.toFixed(2)} por unidad
                                        </span>
                                    </div>
                                ),
                                stock: (
                                    <div className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full animate-pulse ${p.stock > 10 ? 'bg-emerald-500 shadow-emerald-500/50' : p.stock > 0 ? 'bg-amber-500 shadow-amber-500/50' : 'bg-red-500 shadow-red-500/50'}`}></div>
                                        <span className={`font-black text-lg ${p.stock <= 5 ? 'text-red-400' : 'text-white/80'}`}>{p.stock} unidades</span>
                                    </div>
                                ),
                                acciones: (
                                    <div className="flex gap-2">
                                        <button onClick={() => handleOpenModal(p)} className="glass p-3 rounded-xl text-primary-light hover:bg-white/10 transition-all"><Edit2 size={18}/></button>
                                        <button onClick={() => handleDelete(p.id)} className="glass p-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all"><Trash2 size={18}/></button>
                                    </div>
                                )
                            };
                        })}
                    />
                </GlassCard>
            </div>
        </div>

            {/* Modal de Producto */}
            <Modal
                show={showModal}
                onClose={() => setShowModal(false)}
                title={editingProduct ? 'Editar Producto' : 'Nuevo Producto para Retail'}
            >
                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-white/40">Nombre del Producto</label>
                        <input 
                            type="text" 
                            value={formData.nombre} 
                            onChange={e => setFormData({...formData, nombre: e.target.value})} 
                            className="input-field bg-slate-800/80" 
                            placeholder="Ej. Shampoo Anticaída Premium" 
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-white/40 flex items-center gap-1"><Archive size={10}/> Costo Adquisición</label>
                            <input 
                                type="number" 
                                value={formData.precio_costo} 
                                onChange={e => setFormData({...formData, precio_costo: e.target.value})} 
                                className="input-field bg-slate-800/80" 
                                placeholder="0.00"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-1"><DollarSign size={10}/> Precio Venta</label>
                            <input 
                                type="number" 
                                value={formData.precio_venta} 
                                onChange={e => setFormData({...formData, precio_venta: e.target.value})} 
                                className="input-field bg-slate-800/80 font-black text-xl text-emerald-400" 
                                placeholder="0.00"
                            />
                        </div>
                    </div>

                    {formData.precio_costo && formData.precio_venta && (
                        <div className="bg-primary/5 border border-primary/20 p-4 rounded-2xl flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <LineChart className="text-primary-light" size={20} />
                                <div>
                                    <span className="text-[10px] font-black uppercase text-white/40 block">Margen Proyectado</span>
                                    <span className="text-xl font-black text-white">{calculateMargin(formData.precio_costo, formData.precio_venta).toFixed(1)}%</span>
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="text-[10px] font-black uppercase text-white/40 block">Ganancia por Unidad</span>
                                <span className="text-xl font-black text-emerald-400">+${calculateProfit(formData.precio_costo, formData.precio_venta).toFixed(2)}</span>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-6">
                         <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-amber-400 flex items-center gap-1"><Zap size={10}/> Stock Initial</label>
                            <input 
                                type="number" 
                                value={formData.stock} 
                                onChange={e => setFormData({...formData, stock: e.target.value})} 
                                className="input-field bg-slate-800/80" 
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-white/40 flex items-center gap-1"><Barcode size={10}/> Código / SKU</label>
                            <input 
                                type="text" 
                                value={formData.codigo_barras} 
                                onChange={e => setFormData({...formData, codigo_barras: e.target.value})} 
                                className="input-field bg-slate-800/80" 
                                placeholder="Opcional"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-2xl glass border-white/5 mt-4">
                            <div className="flex items-center gap-3">
                                <Plus size={16} className="text-primary-light" />
                                <span className="text-[10px] font-black uppercase text-white/60">Aplica IVU (11.5%)</span>
                            </div>
                            <input 
                            type="checkbox" 
                            checked={formData.aplica_ivu === 1} 
                            onChange={e => setFormData({...formData, aplica_ivu: e.target.checked ? 1 : 0})} 
                            className="w-10 h-5 rounded-full appearance-none bg-white/10 checked:bg-emerald-500 relative transition-colors cursor-pointer before:content-[''] before:absolute before:w-3 before:h-3 before:bg-white before:rounded-full before:top-1 before:left-1 checked:before:left-6 before:transition-all" 
                            />
                    </div>

                    <div className="pt-6 border-t border-white/5">
                        <button 
                            onClick={handleSave}
                            className="premium-btn-primary w-full py-4 text-xs font-black uppercase tracking-widest shadow-xl shadow-primary/20"
                        >
                            {editingProduct ? 'ACTUALIZAR DATOS FINANCIEROS' : 'REGISTRAR PRODUCTO'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Productos;
